import { Link } from '@mui/material';
import React from 'react';
import { INFO } from '#action/types';
import { whitelabel } from '#config/whitelabel';
import { convertTemperature, convertTemperatureUnit, getLinkDateRange } from '#helper';

/**
 * @typedef {Object} Link
 * @property {string} url - link url.
 * @property {string} description - link description.
 */

/**
 * @typedef {Object} Message
 * @property {string} title - message title.
 * @property {string} body - message body.
 * @property {Link} link - message link.
 */

/**
 * Provides methods to send notifications
 */

class NotificationController {
  // Redux store
  static #store = null;

  /**
   * Set Redux store to an internal field.
   * @param {Object} store - Redux store.
   */

  static initStore(store) {
    this.#store = store;
  }

  /**
   * Sends notification based on document visibility state
   * and Notification permission;
   * @param {Object} data - data.
   * @param {number} id - shipment id.
   * @param {string} label - shipment label.
   */

  static sendNotification(data, id, label, target) {
    const { account: { siteMsg } } = this.#store.getState();
    if (siteMsg) {
      const message =
        target === 'appliance' ?
          this.generateApplianceMessage(data, id, label) :
          this.generateShipmentMessage(data, id, label);
      if (document.visibilityState === 'hidden' && Notification.permission === 'granted') {
        this.sendDesktopNotification(message);
      } else {
        this.sendMUINotification(message);
      }
    }
  }

  /**
   * Generates message.
   * @param {Object} data - data.
   * @param {number} shipmentId - shipment id.
   * @param {string} shipmentLabel - shipment label.
   * @returns {Message} message to send.
   */

  static generateShipmentMessage(data, shipmentId, shipmentLabel) {
    const { excursions = [], deviceName = '' } = data;
    const { measurementSystem } = this.#store.getState();
    const title = `Shipment alert: ${shipmentLabel}`;
    const body = {
      header: `Device ${deviceName} has reported ${excursions.length > 1 ? '' : 'an'} excursion${excursions.length > 1 ? 's' : ''}.`,
      excursions: excursions.map(e =>
        ({
          id: e.id,
          label: e.id.charAt(0).toUpperCase() + e.id.slice(1),
          value: e.id === 'temperature' ? `${convertTemperature(e.value, measurementSystem)}${convertTemperatureUnit(measurementSystem)}` : `${e.value}%`,
          link: `${window.origin}/shipment/${shipmentId}/t/${e.tab}`
        }))
    };
    return { title, body };
  }

  /**
 * Generates message.
 * @param {Object} data - data.
 * @param {number} applianceId - shipment id.
 * @param {string} applianceLabel - shipment label.
 * @returns {Message} message to send.
 */

  static generateApplianceMessage(data, applianceId, applianceLabel) {
    const { excursions = [], deviceName = '', applianceCreationDate } = data;
    const { measurementSystem } = this.#store.getState();
    const title = `Application alert: ${applianceLabel}`;
    const body = {
      header: `Device ${deviceName} has reported ${excursions.length > 1 ? '' : 'an'} excursion${excursions.length > 1 ? 's' : ''}.`,
      excursions: excursions.map(e =>
        ({
          id: e.id,
          label: e.id.charAt(0).toUpperCase() + e.id.slice(1),
          value: e.id === 'temperature' ? `${convertTemperature(e.value, measurementSystem)}${convertTemperatureUnit(measurementSystem)}` : `${e.value}%`,
          link: `${window.origin}/warehouse/appliance-overview/${applianceId}/t/${e.tab}${getLinkDateRange(e.timestamp, applianceCreationDate)}`
        }))
    };
    return { title, body };
  }

  /**
   * Sends desktop notification.
   * Attaches notification logo and onclick listener.
   * @param {Object} message - message.
   */

  static sendDesktopNotification(message) {
    const { title, body: { excursions } } = message;
    excursions.forEach(e => {
      const notification = new Notification(title, {
        body: `${e.label}: ${e.value}`,
        icon: whitelabel.buildAssetUrl('media/desktop-notification-logo.png'),
        tag: e.link
      });

      notification.onclick = () => {
        window.open(e.link, '_blank');
      };
    });
  }

  /**
   * Dispatches MUI info notification.
   * @param {Object} message - message.
   */
  static sendMUINotification(message) {
    const { title, body: { header, excursions } } = message;
    if (this.#store) {
      excursions.forEach(e => {
        this.#store.dispatch({
          type: INFO,
          payload: { message: (
            <Link
              href={e.link}
              target="_blank"
              rel="noopener noreferrer"
              style={{ cursor: 'pointer', textDecoration: 'none', color: '#FFF' }}
            >
              <strong>{title}</strong>
              <br />
              {header}
              <br />
              {`${e.label}: ${e.value}`}
            </Link>)
          }
        });
      });
    }
  }
}

export default NotificationController;
